import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { HotelSearchForm } from './HotelSearchForm';
import type { HotelSearchFormValues } from '../types/hotels.presentation';
import {
  CHILDREN_AGES_PARAM,
  formatChildrenAgesParam,
  parseChildrenAgesParam,
} from '../utils/childrenAgesParam';

const HOTEL_SEARCH_ROUTE = '/buscarxhoteles';

const parseNumberParam = (value: string | null, fallback: number) => {
  if (!value) return fallback;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseHotelSearchInitialValues = (
  search: string,
): Partial<HotelSearchFormValues> | undefined => {
  if (!search) return undefined;

  const params = new URLSearchParams(search);

  return {
    adults: parseNumberParam(params.get('adults'), 1),
    checkIn: params.get('checkIn') ?? '',
    checkOut: params.get('checkOut') ?? '',
    children: parseNumberParam(params.get('children'), 0),
    childrenAges: parseChildrenAgesParam(params.get(CHILDREN_AGES_PARAM)),
    countryCode: params.get('countryCode') ?? undefined,
    destination: params.get('destination') ?? '',
    destinationId: params.get('destinationId') ?? undefined,
    rooms: parseNumberParam(params.get('rooms'), 1),
  };
};

interface HotelHeaderSearchProps {
  isLoading?: boolean;
  onSearchStart?: () => void;
  onSearchComplete?: () => void;
  onCloseMobileSearch?: () => void;
}

export interface HotelHeaderSearchHandle {
  triggerSearch: () => void;
}

const buildHotelSearchParams = (query: HotelSearchFormValues) => {
  const params = new URLSearchParams();

  params.set('destination', query.destination.trim());
  if (query.destinationId) params.set('destinationId', query.destinationId);
  if (query.countryCode) params.set('countryCode', query.countryCode);
  params.set('checkIn', query.checkIn);
  params.set('checkOut', query.checkOut);
  params.set('adults', String(query.adults));
  params.set('children', String(query.children ?? 0));
  const childrenAges = formatChildrenAgesParam(query.childrenAges);
  if (childrenAges) params.set(CHILDREN_AGES_PARAM, childrenAges);
  params.set('rooms', String(query.rooms ?? 1));

  return params.toString();
};

const submitForm = (form: HTMLFormElement | null) => {
  if (!form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
    return;
  }

  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
};

export const HotelHeaderSearch = forwardRef<HotelHeaderSearchHandle, HotelHeaderSearchProps>(
  ({ isLoading = false, onSearchStart, onSearchComplete, onCloseMobileSearch }, ref) => {
    const history = useHistory();
    const { search } = useLocation();
    const { registerOnSearch } = useGlobal();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const initialValues = useMemo(() => parseHotelSearchInitialValues(search), [search]);

    const submitSearch = (query: HotelSearchFormValues) => {
      const targetUrl = `${HOTEL_SEARCH_ROUTE}?${buildHotelSearchParams(query)}`;

      setIsSubmitting(true);
      onSearchStart?.();
      history.push(targetUrl);
      registerOnSearch();
      onSearchComplete?.();
      onCloseMobileSearch?.();
      setIsSubmitting(false);
    };

    useImperativeHandle(ref, () => ({
      triggerSearch: () => submitForm(formRef.current),
    }));

    return (
      <HotelSearchForm
        formRef={formRef}
        initialValues={initialValues}
        isSubmitting={isLoading || isSubmitting}
        onSubmit={submitSearch}
      />
    );
  },
);

HotelHeaderSearch.displayName = 'HotelHeaderSearch';
